import { useEffect, useState } from "react";
import {
  Navigate,
  useParams,
  useNavigate,
  useLocation,
} from "react-router-dom";
import { voiNahda, voiKasitella, ID_PROOFING_MAX } from "../utils/oikeudet";
import { formatFinnishDate } from "../utils/dateUtils";
import { useModaaliBackdropSulku } from "../utils/modaaliBackdropSulku";
import styled from "styled-components";
import {
  Heading,
  Button,
  Textarea,
  TextInput,
  InlineAlert,
  Modal,
  ModalContent,
  ModalFooter,
  ModalTitle,
  Text,
  RadioButtonGroup,
  RadioButton,
} from "suomifi-ui-components";
import { IconArrowLeft } from "suomifi-icons";
import { useDispatch } from "react-redux";
import { useAppState } from "../state/store";
import TilaBadge from "../components/TilaBadge";
import KayttajaSidebar from "../components/KayttajaSidebar";
import MuistiinpanoSticky from "../components/MuistiinpanoSticky";
import HenkilollisyydenTodentaminen from "../components/HenkilollisyydenTodentaminen";
import Dokumentit from "../components/Dokumentit";
import SektioHaitari from "../components/SektioHaitari";
import TukipyyntoHistoria from "../components/TukipyyntoHistoria";
import TukipyyntoLaite from "../components/TukipyyntoLaite";
import { naytaNimi } from "../utils/kayttajaNimi";
import { formatAikaleima } from "../utils/format";
import { RIGHT_PANEL_WIDTH } from "../layout/constants";
import {
  backendApi,
  useAssignTicketMutation,
  useGetKayttajaByPersonalIdQuery,
  useGetKayttajaCustomQuery,
  useGetTukipyynnotByKayttajaQuery,
  useGetTukipyyntoQuery,
  useMergeUserAccountsMutation,
  useUnassignTicketMutation,
  useResolveTicketMutation,
  useSendResolvedTicketMutation,
  useApproveDocumentUpdateMutation,
  useDenyDocumentUpdateMutation,
} from "../api/backendApi";
// ─── DEMO: Tilin yhdistäminen (prototyyppidemo — poista testin jälkeen) ───
import { MERGE_AIHE } from "../demo/tilinYhdistaminenHelper";
// ─── /DEMO ───
import { useTodellinenRooli } from "../hooks/useTodellinenRooli";
import { skipToken } from "@reduxjs/toolkit/query";
import { useTranslation } from "react-i18next";
import { Dokumentti, Kayttaja } from "../types";
import DokumenttiKuvat from "../components/DokumenttiKuvat";

// Tukipyyntötyypit, jotka liittyvät ID-proofingin epäonnistumiseen.
// Näille pyynnöille HenkilollisyydenTodentaminen-sektio nostetaan suoraan
// tukipyyntönäkymään, jotta virkailijan ei tarvitse hypätä käyttäjäkortille.
const ID_PROOFING_TYYPIT: ReadonlyArray<string> = [
  "NFC-luku epäonnistui",
  "MRZ-luku epäonnistui",
  "Kasvovertailu epäonnistui",
  "Päivitetyn asiakirjan tiedot eivät täsmää",
];

const Page = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.s};
`;

const HeaderArea = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.xs};
`;

const BackButton = styled.button`
  background: transparent;
  border: 0;
  padding: 0;
  font: ${({ theme }) => theme.typography.bodyTextSmall};
  font-size: 0.875rem;
  color: ${({ theme }) => theme.colors.highlightBase};
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xxs};
  align-self: flex-start;

  & > svg {
    width: 1rem;
    height: 1rem;
  }

  &:hover {
    text-decoration: underline;
    color: ${({ theme }) => theme.colors.highlightDark1};
  }
`;

const TikettiHeader = styled.div`
  font-family: "Courier New", monospace;
  font-size: 0.875rem;
  color: ${({ theme }) => theme.colors.brandBase};
  font-weight: 600;
`;

const MetaRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.s};
  font: ${({ theme }) => theme.typography.bodyTextSmall};
  font-size: 0.8125rem;
  color: ${({ theme }) => theme.colors.depthDark1};
`;

/* Käsittelijä-paneeli sivupalkissa: dropdown nykyiselle käsittelijälle +
   tertiary-tyylinen "Ota itselle" / "Palauta avoimiin" -toiminto sen alla.
   Aiemmin tukipyynnön sisällön ylälaitaa hallitsi leveä KasittelijaBar —
   siirretty sivupalkkiin jotta sisältökortit nousevat ylemmäs (C12-C13). */
const KasittelijaPanel = styled.section`
  background: ${({ theme }) => theme.colors.whiteBase};
  border: 1px solid ${({ theme }) => theme.colors.depthLight1};
  padding: ${({ theme }) => theme.spacing.m};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.s};
`;

const KasittelijaLabel = styled.div`
  font: ${({ theme }) => theme.typography.bodySemiBoldSmall};
  font-size: 0.6875rem;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: ${({ theme }) => theme.colors.depthDark1};
`;

const EiKasittelijaa = styled.div`
  font-style: italic;
  color: ${({ theme }) => theme.colors.depthDark1};
  font-size: 0.875rem;
`;

const KasittelijaNimi = styled.div`
  color: ${({ theme }) => theme.colors.blackBase};
  font-size: 0.875rem;
`;

/* Tertiary-tyylin "Ota itselle" / "Palauta avoimiin": pienempi koko ja
   secondaryNoBorder, jotta paneeli pysyy kompaktina. */
const KasittelijaToiminto = styled.div`
  align-self: flex-start;

  & .fi-button {
    min-height: 28px;
    padding: 4px 10px;
    font-size: 0.8125rem;
  }
`;

const Layout = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.s};
  align-items: flex-start;
`;

const Vasen = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.s};
`;

const Oikea = styled.div`
  width: ${RIGHT_PANEL_WIDTH};
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.s};
`;

const Panel = styled.section`
  background: ${({ theme }) => theme.colors.whiteBase};
  border: 1px solid ${({ theme }) => theme.colors.depthLight1};
  padding: ${({ theme }) => theme.spacing.m};
`;

const PanelTitle = styled.div`
  font: ${({ theme }) => theme.typography.bodySemiBoldSmall};
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: ${({ theme }) => theme.colors.brandBase};
  border-bottom: 2px solid ${({ theme }) => theme.colors.brandBase};
  padding-bottom: ${({ theme }) => theme.spacing.xs};
  margin-bottom: ${({ theme }) => theme.spacing.s};
`;

const PanelHeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 2px solid ${({ theme }) => theme.colors.brandBase};
  padding-bottom: ${({ theme }) => theme.spacing.xs};
  margin-bottom: ${({ theme }) => theme.spacing.m};
`;

const PanelHeaderTitle = styled.span`
  font: ${({ theme }) => theme.typography.bodySemiBoldSmall};
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: ${({ theme }) => theme.colors.brandBase};
`;

const PanelHeaderMerkinta = styled.span`
  font-family: "Courier New", monospace;
  font-size: 0.75rem;
  color: ${({ theme }) => theme.colors.depthDark1};
`;

const QuoteBox = styled.div`
  background: ${({ theme }) => theme.colors.depthLight3};
  border-left: 4px solid ${({ theme }) => theme.colors.depthLight1};
  padding: ${({ theme }) => theme.spacing.s} ${({ theme }) => theme.spacing.m};
  font: ${({ theme }) => theme.typography.bodyText};
  font-size: 0.9375rem;
  color: ${({ theme }) => theme.colors.blackBase};
  white-space: pre-wrap;
`;

const EmptyMsg = styled.p`
  font: ${({ theme }) => theme.typography.bodyTextSmall};
  color: ${({ theme }) => theme.colors.depthDark1};
  font-style: italic;
  margin: 0;
`;

const Actions = styled.div`
  margin-top: ${({ theme }) => theme.spacing.s};
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing.s};
`;

/* S2: info-teksti kun toinen käsittelijä jo työn alla */
const OtaKasittelyynInfo = styled.p`
  font: ${({ theme }) => theme.typography.bodyTextSmall};
  font-size: 0.875rem;
  color: ${({ theme }) => theme.colors.depthDark1};
  margin: 0;
  padding: ${({ theme }) => theme.spacing.s} 0;
`;

const RatkaistuViesti = styled.div`
  font: ${({ theme }) => theme.typography.bodyTextSmall};
  font-size: 0.875rem;
  color: ${({ theme }) => theme.colors.successDark1};
`;

const VastausOtsikko = styled.div`
  font: ${({ theme }) => theme.typography.bodySemiBoldSmall};
  font-size: 0.75rem;
  color: ${({ theme }) => theme.colors.depthDark1};
  margin-top: ${({ theme }) => theme.spacing.s};
  margin-bottom: ${({ theme }) => theme.spacing.xxs};
`;

const NotFoundBox = styled.div`
  padding: ${({ theme }) => theme.spacing.xxl};
  color: ${({ theme }) => theme.colors.depthDark1};
`;

/* ─── DEMO: Tilin yhdistäminen — yhdistämislohkon tyylit ─── */
/* Lyhyt ohjeteksti yhdistämiskortin yläosaan (ennen hakulinkkiä). */
const Ohje = styled.p`
  font: ${({ theme }) => theme.typography.bodyTextSmall};
  font-size: 0.875rem;
  color: ${({ theme }) => theme.colors.depthDark1};
  margin: 0 0 ${({ theme }) => theme.spacing.xs};
`;

/* Käyttäjähaku-linkki lohkon ylimpänä: avaa haun uuteen välilehteen, jotta
   virkailija löytää oikean vanhan tilin ilman että tukipyyntö sulkeutuu. */
const HakuLinkki = styled.a`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xxs};
  margin-bottom: ${({ theme }) => theme.spacing.m};
  font: ${({ theme }) => theme.typography.bodyTextSmall};
  font-size: 0.875rem;
  color: ${({ theme }) => theme.colors.highlightBase};

  &:hover {
    text-decoration: underline;
    color: ${({ theme }) => theme.colors.highlightDark1};
  }
`;

const UidKentta = styled.div`
  max-width: 22rem;

  & input {
    font-family: "Courier New", monospace;
  }
`;

/* Väli radiovalintojen ja niiden alle avautuvien kenttien välissä. */
const ValinnanKentat = styled.div`
  margin-top: ${({ theme }) => theme.spacing.m};
`;

/* Väli UID-kentän (ja mahdollisen osuma-ilmoituksen) sekä vastauskentän välissä. */
const VastausKentta = styled.div`
  margin-top: ${({ theme }) => theme.spacing.m};
`;
/* ─── /DEMO ─── */

export default function TukipyyntoNakyma() {
  const { t } = useTranslation(["tukipyyntoNakyma", "common"]);
  const [kohde, setKohde] = useState<Kayttaja | undefined>(undefined);
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { state, dispatch } = useAppState();
  const {
    kayttaja: virkailija,
    todellinenRooli,
    isLoading: rooliLataa,
  } = useTodellinenRooli();
  const [assignTicket] = useAssignTicketMutation();
  const [unassignTicket] = useUnassignTicketMutation();
  const { data: tukipyynto, isLoading: tukipyyntoLataa } =
    useGetTukipyyntoQuery(id!);

  const { data: kayttaja, isLoading: kayttajaLataa } =
    useGetKayttajaCustomQuery(tukipyynto?.kayttajaUid ?? skipToken);
  const { data: kayttajanTukipyynnot = [] } = useGetTukipyynnotByKayttajaQuery(
    tukipyynto?.kayttajaUid ?? skipToken,
  );

  // ─── DEMO: Tilin yhdistäminen ───
  const reduxDispatch = useDispatch();
  // Käyttäjälista kohteen UID-hakuun (sisältää injektoidut demokäyttäjät).

  const [mergeAccounts] = useMergeUserAccountsMutation();
  const [resolveTicket] = useResolveTicketMutation();
  const [sendResolvedTicket] = useSendResolvedTicketMutation();
  const [approveDocumentUpdate] = useApproveDocumentUpdateMutation();
  const [denyDocumentUpdate] = useDenyDocumentUpdateMutation();

  const [documentUpdateValinta, setDocumentUpdateValinta] = useState<
    "approve" | "deny" | ""
  >("");

  const [documentUpdateConfirmAuki, setDocumentUpdateConfirmAuki] =
    useState(false);
  useModaaliBackdropSulku(documentUpdateConfirmAuki, () =>
    setDocumentUpdateConfirmAuki(false),
  );

  const voikoKasitella = voiKasitella(todellinenRooli?.profiili);
  // Käyttöoikeus-suoja: rooli joka ei saa avata tukipyyntöä → etusivulle

  /* listSearch + listScrollY: jos käyttäjä saapui tukipyyntölistalta,
     lista kuljetti query-stringinsä JA tarkan scroll-positionsa
     location.state:ssa. Takaisin-painike palauttaa täsmälleen saman
     filtteritilan JA tarkan vieritysasennon — "lista näyttää identtiseltä
     kuin oli ennen tukipyynnön avaamista" (GitHub/Linear-malli).
     Fallback tyhjä string + 0 → lista oletustilaan, scroll alkuun. */
  const palautusTila = location.state as {
    listSearch?: string;
    listScrollY?: number;
    // Käyttäjäkortilta avattu tukipyyntö (MR5): paluu vie takaisin kortille,
    // ei tukipyyntölistalle.
    fromKayttajaUid?: string;
  } | null;
  const listSearch = palautusTila?.listSearch ?? "";
  const listScrollY = palautusTila?.listScrollY;
  const fromKayttajaUid = palautusTila?.fromKayttajaUid;

  function palaaListalle() {
    navigate(`/tukipyynnot${listSearch}`, {
      state: { palauttavaRivi: id, listScrollY },
    });
  }

  /* Kontekstitietoinen paluu (MR5): käyttäjäkortilta avattu tukipyyntö → takaisin
     kortille selaimen historiassa (navigate(-1) säilyttää kortin oman
     paluukontekstin, jolloin sen "Takaisin hakutuloksiin" toimii). Muuten →
     tukipyyntölistalle palautetulla filtteri- + scroll-tilalla. */
  function palaa() {
    if (fromKayttajaUid) navigate(-1);
    else palaaListalle();
  }

  const [vastausTeksti, setVastausTeksti] = useState("");
  const [ilmoitus, setIlmoitus] = useState<string | null>(null);
  const [confirmAuki, setConfirmAuki] = useState(false);
  const [bulkConfirmAuki, setBulkConfirmAuki] = useState(false);
  useModaaliBackdropSulku(confirmAuki, () => setConfirmAuki(false));
  useModaaliBackdropSulku(bulkConfirmAuki, () => setBulkConfirmAuki(false));

  // ─── DEMO: Tilin yhdistäminen — yhdistämislohkon tila ───
  const [mergeValinta, setMergeValinta] = useState<"yhdista" | "ei" | "">("");
  const [kohdeUid, setKohdeUid] = useState("");

  // Replace the old useGetKayttajatQuery call with:
  const {
    data: hakuTulos,
    isLoading: haetaanKayttajaa,
    error: hakuVirhe,
  } = useGetKayttajaByPersonalIdQuery(
    kohdeUid.trim() ? kohdeUid.trim() : skipToken,
  );

  const [mergeConfirmAuki, setMergeConfirmAuki] = useState(false);
  useModaaliBackdropSulku(mergeConfirmAuki, () => setMergeConfirmAuki(false));
  // ─── /DEMO ───

  // SPA-reset: kun :id-parametri vaihtuu (siirrytään toiseen tukipyyntöön
  // ilman komponentin unmounttia), nollataan paikalliset tilatiedot —
  // erityisesti ratkaisu-ilmoitus, joka muuten kantautuu mukana.
  useEffect(() => {
    setIlmoitus(null);
    setVastausTeksti("");
    setConfirmAuki(false);
    setBulkConfirmAuki(false);
    // DEMO: Tilin yhdistäminen — nollaa yhdistämislohko
    setMergeValinta("");
    setKohdeUid("");
    setMergeConfirmAuki(false);
    setDocumentUpdateValinta(""); // ✅ Reset document update state
    setDocumentUpdateConfirmAuki(false);
  }, [id]);

  async function handleDenyDocumentUpdate() {
    if (
      !tukipyynto ||
      !kayttaja ||
      !newestDocumentUpdateTapahtuma ||
      !vastausTeksti.trim()
    )
      return;

    try {
      await denyDocumentUpdate({
        uid: kayttaja.uid,
        ticketId: tukipyynto.tikettinumero.replace("#", ""),
        tapahtuma: newestDocumentUpdateTapahtuma,
        response: vastausTeksti.trim(),
      }).unwrap();

      setDocumentUpdateConfirmAuki(false);
      setVastausTeksti("");
      setDocumentUpdateValinta("");
      setIlmoitus(t("asiakirjan_paivitys.hylatty_ilmoitus"));

      reduxDispatch(backendApi.util.invalidateTags(["Ticket"]));
    } catch (error) {
      console.error("Deny failed:", error);
      setIlmoitus(t("asiakirjan_paivitys.hylatty_epaonnistui"));
    }
  }

  const newestDocumentUpdateTapahtuma = kayttaja?.tapahtumat
    ?.filter((t) => t.tyyppi === "asiakirjan_paivitys")
    ?.sort(
      (a, b) => new Date(b.aika).getTime() - new Date(a.aika).getTime(),
    )[0];

  const dokumenttiTiedotFromTapahtuma: Dokumentti | undefined =
    newestDocumentUpdateTapahtuma &&
    newestDocumentUpdateTapahtuma.dokumenttiTiedot
      ? {
          tyyppi:
            (newestDocumentUpdateTapahtuma.dokumenttiTiedot.tyyppi as any) ||
            "passi",
          maakoodi:
            newestDocumentUpdateTapahtuma.dokumenttiTiedot.maakoodi || "",
          numero: newestDocumentUpdateTapahtuma.dokumenttiTiedot.numero || "",
          vanhenee:
            newestDocumentUpdateTapahtuma.dokumenttiTiedot.vanhenee || "",
          etunimi: newestDocumentUpdateTapahtuma.dokumenttiTiedot.etunimi || "",
          sukunimi:
            newestDocumentUpdateTapahtuma.dokumenttiTiedot.sukunimi || "",
          syntymaaika:
            newestDocumentUpdateTapahtuma.dokumenttiTiedot.syntymaaika || "",
          sukupuoli:
            newestDocumentUpdateTapahtuma.dokumenttiTiedot.sukupuoli || "",
          kansalaisuus:
            newestDocumentUpdateTapahtuma.dokumenttiTiedot.kansalaisuus || "",
          tila: "voimassa",
        }
      : undefined;

  async function handleApproveDocumentUpdate() {
    if (
      !tukipyynto ||
      !kayttaja ||
      !newestDocumentUpdateTapahtuma ||
      !vastausTeksti.trim()
    )
      return;

    try {
      await approveDocumentUpdate({
        uid: kayttaja.uid,
        ticketId: tukipyynto.tikettinumero.replace("#", ""),
        tapahtuma: newestDocumentUpdateTapahtuma,
        response: vastausTeksti.trim(),
      }).unwrap();

      setDocumentUpdateConfirmAuki(false);
      setVastausTeksti("");
      setDocumentUpdateValinta("");
      setIlmoitus(t("asiakirjan_paivitys.hyvatty_ilmoitus"));

      reduxDispatch(backendApi.util.invalidateTags(["Ticket"]));
    } catch (error) {
      console.error("Approve failed:", error);
      setIlmoitus(t("asiakirjan_paivitys.hyvatty_epaonnistui"));
    }
  }

  useEffect(() => {
    if (hakuTulos) {
      setKohde(hakuTulos);
    } else if (kohdeUid.trim() && !haetaanKayttajaa && hakuVirhe) {
      setKohde(undefined); // Clear if user not found
    }
  }, [hakuTulos, kohdeUid, haetaanKayttajaa, hakuVirhe]);

  if (rooliLataa || kayttajaLataa || tukipyyntoLataa) return <>Ladataan...</>;
  if (!voiNahda("tukipyynnot.avaa_yksittainen", todellinenRooli?.profiili)) {
    return <Navigate to="/" replace />;
  }

  if (!tukipyynto || !kayttaja) {
    return <NotFoundBox>Tukipyyntöä ei löydy.</NotFoundBox>;
  }
  const naytaIdProofing = ID_PROOFING_TYYPIT.includes(tukipyynto.aihe);

  // Asiakirjalta luetut tiedot tulevat sirusta (siru-vaihe). Kortti näytetään
  // vain kun siru on jossain yrityksessä onnistunut: joko koko todennus onnistui
  // tai epäonnistuminen tuli vasta myöhemmässä kuva-vaiheessa. Jos siru ei ole
  // onnistunut, asiakirjalta ei ole voitu lukea mitään.
  const naytaAsiakirjaltaLuettu = kayttaja.tapahtumat.some(
    (tapahtuma) =>
      tapahtuma?.tyyppi &&
      (tapahtuma.tyyppi === "id_proofing_onnistui" ||
        tapahtuma.tyyppi === "id_proofing_epaonnistui" ||
        tapahtuma.tyyppi === "asiakirjan_paivitys") &&
      (tapahtuma.onnistui === true ||
        tapahtuma.epaonnistunutVaihe === "kuva" ||
        tapahtuma.epaonnistunutVaihe === "tarkistus"),
  );

  // Laite-kortin tiedot. Ensisijaisesti tiketin oma laite (kun backend tuo sen).
  // ID-proofing-tikettien osalta laite tiedetään todennusyrityksen laitteesta:
  // samaan proofing-virheeseen tukipyyntöä ei voi lähettää muulta laitteelta,
  // joten se on sama laite (ei arvaus). Mäpätään käyttäjän laite kortin muotoon.
  const proofingLaite = (() => {
    const yritys = [...kayttaja.tapahtumat]
      .filter(
        (tap) =>
          tap?.tyyppi &&
          (tap.tyyppi === "asiakirjan_paivitys" ||
            tap.tyyppi.startsWith("id_proofing")) &&
          tap.laiteId,
      )
      .sort((a, b) => (a.aika < b.aika ? 1 : -1))[0];
    const d = yritys?.laiteId
      ? kayttaja.laitteet.find((l) => l.id === yritys.laiteId)
      : undefined;
    return d
      ? {
          malli: d.nimi,
          os: d.kayttojarjestelma,
          sovellusversio: d.sovellusversio ?? "—",
        }
      : undefined;
  })();
  const laiteTiedot = tukipyynto.laite ?? proofingLaite;

  const onMinunKasittelyssani = tukipyynto.kasittelijaUid === virkailija?.id;
  const onRatkaistu =
    tukipyynto.tila === "ratkaistu" || tukipyynto.tila === "self-resolved";

  const onDokumentinPaivitys =
    tukipyynto.aihe === "Päivitetyn asiakirjan tiedot eivät täsmää";
  const onMerge = tukipyynto.aihe === MERGE_AIHE;
  const virkailijaNimi = `${virkailija?.name.givenName ?? ""} ${
    virkailija?.name.familyName ?? ""
  }`.trim();
  const yhdistaminenTehty = onMerge && kayttaja.henkilollisyysTodennettu;

  const kayttajanKaikkiAvoimet = kayttajanTukipyynnot.filter(
    (t) => t.tila === "avoin" || t.tila === "käsittelyssä",
  );
  const kayttajanAvoinMaara = kayttajanKaikkiAvoimet.length;

  const kaikkiOmassaKasittelyssa =
    kayttajanAvoinMaara >= 2 &&
    kayttajanKaikkiAvoimet.every((t) => t.kasittelijaUid === virkailija?.id);

  async function handleOtaItselle() {
    if (!tukipyynto) return;
    await assignTicket(tukipyynto.tikettinumero.replace("#", "")).unwrap();
  }

  async function handlePalautaAvoimiin() {
    if (!tukipyynto) return;
    await unassignTicket(tukipyynto.tikettinumero.replace("#", "")).unwrap();
  }

  async function handleYhdista() {
    if (!tukipyynto || !kayttaja || !kohde) {
      setMergeConfirmAuki(false);
      return;
    }

    try {
      await mergeAccounts({
        sourceUid: kayttaja.uid,
        targetUid: kohde.uid,
        reason: vastausTeksti.trim() || t("merge.oletusvastaus"),
      }).unwrap();

      await sendResolvedTicket({
        ticketId: tukipyynto.tikettinumero.replace("#", ""),
        userEmail: kayttaja.sahkoposti,
        userName: naytaNimi(kayttaja),
        response: vastausTeksti.trim(),
      }).unwrap();

      setMergeConfirmAuki(false);
      setVastausTeksti("");
      setKohdeUid("");
      setMergeValinta("");
      setIlmoitus(t("merge.onnistui_ilmoitus"));

      // Refresh the ticket to show it's resolved
      reduxDispatch(backendApi.util.invalidateTags(["Ticket"]));
    } catch (error) {
      console.error("Merge failed:", error);
      setIlmoitus(t("merge.epaonnistui"));
    }
  }

  async function handleAlaYhdista() {
    if (!tukipyynto || !kayttaja || !vastausTeksti.trim()) return;

    // Just resolve without merging
    await sendResolvedTicket({
      ticketId: tukipyynto.tikettinumero.replace("#", ""),
      userEmail: kayttaja.sahkoposti,
      userName: naytaNimi(kayttaja),
      response: vastausTeksti.trim(),
    }).unwrap();

    setMergeConfirmAuki(false);
    setVastausTeksti("");
    setKohdeUid("");
    setMergeValinta("");
    setIlmoitus(t("merge.ratkaistu_ilman"));
  }
  async function handleLahetaJaRatkaise() {
    if (!tukipyynto || !vastausTeksti.trim() || !kayttaja) return;

    try {
      // Send email and resolve ticket
      await sendResolvedTicket({
        ticketId: tukipyynto.tikettinumero.replace("#", ""),
        userEmail: kayttaja.sahkoposti,
        userName: naytaNimi(kayttaja),
        response: vastausTeksti.trim(),
      }).unwrap();

      // ✅ Only reached if successful
      setConfirmAuki(false);
      setVastausTeksti("");
      setIlmoitus("Tukipyyntö ratkaistu. Viesti lähetetty käyttäjälle.");

      // Refresh tickets
      reduxDispatch(backendApi.util.invalidateTags(["Ticket"]));
    } catch (error: any) {
      // ✅ Handle errors
      console.error("Failed to send resolved ticket:", error);

      const errorMsg =
        error?.status === 404
          ? "Tukipyyntöä ei löytynyt. Yritä päivittää sivu."
          : error?.data?.message ||
            "Viestin lähettäminen epäonnistui. Yritä uudelleen.";

      setIlmoitus(errorMsg);
    }
  }

  function handleLahetaJaRatkaiseKaikki() {
    if (!tukipyynto || !kayttaja || !vastausTeksti.trim()) return;
    dispatch({
      type: "LAHETA_JA_RATKAISE_KAIKKI",
      kayttajaUid: kayttaja.uid,
      vastaus: vastausTeksti.trim(),
      virkailijaUid: virkailija?.id ?? "",
      virkailijaNimi:
        virkailija?.name.givenName + " " + virkailija?.name.familyName,
    });
    setBulkConfirmAuki(false);
    setVastausTeksti("");
    setIlmoitus(`${kayttajanAvoinMaara} ${t("ilmoitus.ratkaistu")}`);
  }

  const muistiinpanotToteutettu = false;

  return (
    <Page>
      <HeaderArea>
        <BackButton onClick={palaa} type="button">
          <IconArrowLeft aria-hidden />
          {fromKayttajaUid
            ? t("buttons.takaisin.kayttajakortille")
            : t("buttons.takaisin.listaan")}
        </BackButton>
        <Heading variant="h1">
          {t(`common:tukipyynto.aihe.${tukipyynto.aihe}`)}
        </Heading>
        <MetaRow>
          <TikettiHeader>{tukipyynto.tikettinumero}</TikettiHeader>
          <TilaBadge tila={tukipyynto.tila} />
          <span>
            {t("tapahtumat.saapunut")}: {formatFinnishDate(tukipyynto.saapunut)}
          </span>
          <span>·</span>
          {onRatkaistu ? (
            <span>
              {t("tapahtumat.ratkaistu")}:{" "}
              {formatFinnishDate(tukipyynto.paivitetty)}
              {tukipyynto.kasittelijaNimi
                ? ` · ${tukipyynto.kasittelijaNimi}`
                : ""}
            </span>
          ) : (
            <span>
              {t("tapahtumat.viimeisin")}:{" "}
              {formatFinnishDate(tukipyynto.paivitetty)}
            </span>
          )}
        </MetaRow>
      </HeaderArea>

      {ilmoitus && (
        <InlineAlert
          labelText={
            ilmoitus.includes("epäonnistui") ||
            ilmoitus.includes("ei löytynyt") ||
            ilmoitus.includes("error") ||
            ilmoitus.includes("virhe")
              ? t("common:alert.virhe")
              : t("common:alert.onnistui")
          }
          status={
            ilmoitus.includes("epäonnistui") ||
            ilmoitus.includes("error") ||
            ilmoitus.includes("ei löytynyt") ||
            ilmoitus.includes("virhe")
              ? "error"
              : "neutral"
          }
        >
          {ilmoitus}
        </InlineAlert>
      )}

      <Layout>
        <Vasen>
          <SektioHaitari otsikko={t("otsikot.kayttajan_viesti")} defaultOpen>
            {tukipyynto.alkuperainenViesti ? (
              <QuoteBox>{tukipyynto.alkuperainenViesti}</QuoteBox>
            ) : (
              <EmptyMsg>{t("ei.viestia")}</EmptyMsg>
            )}
          </SektioHaitari>

          {(onDokumentinPaivitys && dokumenttiTiedotFromTapahtuma) ||
          (naytaAsiakirjaltaLuettu && !onDokumentinPaivitys) ? (
            <SektioHaitari
              otsikko={t("asiakirjaLuetutTiedot.otsikko")}
              defaultOpen
            >
              <Dokumentit
                uid={kayttaja.uid}
                dokumentit={
                  onDokumentinPaivitys && dokumenttiTiedotFromTapahtuma
                    ? [dokumenttiTiedotFromTapahtuma]
                    : kayttaja.dokumentit
                }
                naytaTag={false}
                naytaViivatKunTyhja
              />
              {newestDocumentUpdateTapahtuma && (
                <DokumenttiKuvat
                  uid={kayttaja.uid}
                  dokumenttiEtupuoliFileName={
                    newestDocumentUpdateTapahtuma.dokumenttiTiedot
                      ?.dokumenttiEtupuoliFileName
                  }
                  dokumenttiTakapuoliFileName={
                    newestDocumentUpdateTapahtuma.dokumenttiTiedot
                      ?.dokumenttiTakapuoliFileName
                  }
                  selfieFileName={
                    newestDocumentUpdateTapahtuma.dokumenttiTiedot
                      ?.selfieFileName
                  }
                />
              )}
            </SektioHaitari>
          ) : null}

          {/* DEMO: Tilin yhdistäminen — lähteen biometrinen todennus näkyviin
              (merge-tiketin tyyppi ei muuten laukaise naytaIdProofing-korttia). */}
          {onMerge && !naytaIdProofing && kayttaja.idProofingYritykset > 0 && (
            <SektioHaitari
              otsikko={t("otsikot.henkilollisyys")}
              merkinta={`${kayttaja.idProofingYritykset}`}
              defaultOpen
            >
              <HenkilollisyydenTodentaminen kayttaja={kayttaja} />
            </SektioHaitari>
          )}

          {naytaIdProofing && (
            <SektioHaitari
              otsikko={t("otsikot.henkilollisyys")}
              merkinta={`${kayttaja.idProofingYritykset}`}
              defaultOpen
            >
              <HenkilollisyydenTodentaminen kayttaja={kayttaja} />
            </SektioHaitari>
          )}

          {/* Laite: Henkilöllisyys-kortin jälkeen, oletuksena kiinni
              (hiekkalaatikon korttijärjestys). Laite tiedetään todennusyrityksen
              laitteesta (= sama laite jolta tukipyyntö lähetettiin). */}
          {laiteTiedot && (
            <SektioHaitari otsikko={t("otsikot.laite")}>
              <TukipyyntoLaite laite={laiteTiedot} />
            </SektioHaitari>
          )}

          <SektioHaitari
            otsikko={t("otsikot.kasittelyhistoria")}
            merkinta={`${tukipyynto.kasittelyhistoria.length} ${
              tukipyynto.kasittelyhistoria.length === 1
                ? t("merkinnat.yksi")
                : t("merkinnat.monta")
            }`}
          >
            <TukipyyntoHistoria historia={tukipyynto.kasittelyhistoria} />
          </SektioHaitari>

          {!voikoKasitella ? (
            <></>
          ) : onDokumentinPaivitys && !onRatkaistu ? (
            // ✅ Document update section
            <SektioHaitari
              otsikko={t("asiakirjan_paivitys.otsikko")}
              defaultOpen
            >
              {onMinunKasittelyssani ? (
                <>
                  <RadioButtonGroup
                    labelText={t("asiakirjan_paivitys.valinta")}
                    labelMode="hidden"
                    name="document-update-valinta"
                    value={documentUpdateValinta}
                    onChange={(v: string) =>
                      setDocumentUpdateValinta(v as "approve" | "deny")
                    }
                  >
                    <RadioButton
                      value="approve"
                      hintText={t("asiakirjan_paivitys.hyvatty_ohje")}
                    >
                      {t("asiakirjan_paivitys.hyvatty")}
                    </RadioButton>
                    <RadioButton
                      value="deny"
                      hintText={t("asiakirjan_paivitys.hylatty_ohje")}
                    >
                      {t("asiakirjan_paivitys.hylatty")}
                    </RadioButton>
                  </RadioButtonGroup>

                  {documentUpdateValinta && (
                    <ValinnanKentat>
                      <Textarea
                        labelText={t("labels.sahkoposti_viesti")}
                        hintText={`${t("labels.lahetetaan_osoitteeseen")} ${
                          kayttaja.sahkoposti
                        }`}
                        value={vastausTeksti}
                        onChange={(e) => setVastausTeksti(e.target.value)}
                        rows={5}
                        fullWidth
                      />
                      <Actions>
                        <Button
                          onClick={() => setDocumentUpdateConfirmAuki(true)}
                          disabled={!vastausTeksti.trim()}
                        >
                          {t("asiakirjan_paivitys.laheta_nappi")}
                        </Button>
                      </Actions>
                    </ValinnanKentat>
                  )}
                </>
              ) : tukipyynto.kasittelijaUid &&
                tukipyynto.kasittelijaUid !== virkailija?.id ? (
                <OtaKasittelyynInfo>
                  {tukipyynto.kasittelijaNimi ?? "Toinen virkailija"}{" "}
                  {t("tekstit.kasittelee")}
                </OtaKasittelyynInfo>
              ) : (
                <Actions>
                  <Button onClick={handleOtaItselle}>
                    {t("buttons.ota_kasittelyyn")}
                  </Button>
                </Actions>
              )}
            </SektioHaitari>
          ) : onMerge && !onRatkaistu ? (
            <SektioHaitari otsikko={t("merge.otsikko")} defaultOpen>
              {onMinunKasittelyssani ? (
                <>
                  <Ohje>{t("merge.ohje")}</Ohje>
                  <HakuLinkki
                    href="/kayttajahaku"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {t("merge.avaa_haku")}
                  </HakuLinkki>
                  <RadioButtonGroup
                    labelText={t("merge.valinta")}
                    labelMode="hidden"
                    name="merge-valinta"
                    value={mergeValinta}
                    onChange={(v: string) =>
                      setMergeValinta(v as "yhdista" | "ei")
                    }
                  >
                    <RadioButton
                      value="yhdista"
                      hintText={t("merge.yhdista_ohje")}
                    >
                      {t("merge.yhdista")}
                    </RadioButton>
                    <RadioButton
                      value="ei"
                      hintText={t("merge.ala_yhdista_ohje")}
                    >
                      {t("merge.ala_yhdista")}
                    </RadioButton>
                  </RadioButtonGroup>

                  {mergeValinta === "yhdista" && (
                    <ValinnanKentat>
                      <UidKentta>
                        <TextInput
                          labelText={t("merge.uid_label")}
                          value={kohdeUid}
                          onChange={(v) => setKohdeUid((v ?? "").toString())}
                          status={
                            kohdeUid.trim() && !haetaanKayttajaa && hakuVirhe
                              ? "error"
                              : "default"
                          }
                          statusText={
                            kohdeUid.trim() && !haetaanKayttajaa && hakuVirhe
                              ? t("merge.user_not_found")
                              : ""
                          }
                          fullWidth
                        />
                        {haetaanKayttajaa && kohdeUid.trim() && (
                          <Text>{t("common:ladataan")}</Text>
                        )}
                      </UidKentta>
                      {kohdeUid.trim() &&
                        (kohde ? (
                          <InlineAlert
                            status="neutral"
                            labelText={t("merge.osuma")}
                          >
                            {naytaNimi(kohde)} · {kohde.sahkoposti}
                          </InlineAlert>
                        ) : (
                          <InlineAlert
                            status="warning"
                            labelText={t("merge.ei_osumaa")}
                          >
                            {t("merge.ei_osumaa_teksti")}
                          </InlineAlert>
                        ))}
                      <VastausKentta>
                        <Textarea
                          labelText={t("labels.sahkoposti_viesti")}
                          hintText={`${t("labels.lahetetaan_osoitteeseen")} ${
                            kayttaja.sahkoposti
                          } ${t("labels.mock_viesti")}`}
                          value={vastausTeksti}
                          onChange={(e) => setVastausTeksti(e.target.value)}
                          rows={5}
                          fullWidth
                        />
                      </VastausKentta>
                      <Actions>
                        <Button
                          onClick={() => setMergeConfirmAuki(true)}
                          disabled={!kohdeUid.trim()}
                        >
                          {t("merge.vahvista_nappi")}
                        </Button>
                      </Actions>
                    </ValinnanKentat>
                  )}

                  {mergeValinta === "ei" && (
                    <ValinnanKentat>
                      <Textarea
                        labelText={t("labels.sahkoposti_viesti")}
                        hintText={`${t("labels.lahetetaan_osoitteeseen")} ${
                          kayttaja.sahkoposti
                        } ${t("labels.mock_viesti")}`}
                        value={vastausTeksti}
                        onChange={(e) => setVastausTeksti(e.target.value)}
                        rows={5}
                        fullWidth
                      />
                      <Actions>
                        <Button
                          onClick={() => setMergeConfirmAuki(true)}
                          disabled={!vastausTeksti.trim()}
                        >
                          {t("merge.ratkaise_nappi")}
                        </Button>
                      </Actions>
                    </ValinnanKentat>
                  )}
                </>
              ) : tukipyynto.kasittelijaUid &&
                tukipyynto.kasittelijaUid !== virkailija?.id ? (
                <OtaKasittelyynInfo>
                  {tukipyynto.kasittelijaNimi ?? "Toinen virkailija"}{" "}
                  {t("tekstit.kasittelee")}
                </OtaKasittelyynInfo>
              ) : (
                <Actions>
                  <Button onClick={handleOtaItselle}>
                    {t("buttons.ota_kasittelyyn")}
                  </Button>
                </Actions>
              )}
            </SektioHaitari>
          ) : !onRatkaistu ? (
            <SektioHaitari otsikko={t("otsikot.vastaa_ratkaise")} defaultOpen>
              {/* S2 (10.6. B1): vastauslaatikko vasta kun tukipyyntö on otettu
                  itselle. Ennen sitä näytetään vain "Ota käsittelyyn" -nappi.
                  Jos toisella käsittelijällä → näytetään tieto mutta ei omaa
                  toimintoa. */}
              {onMinunKasittelyssani ? (
                <>
                  <Textarea
                    labelText={t("labels.sahkoposti_viesti")}
                    hintText={`${t("labels.lahetetaan_osoitteeseen")} ${
                      kayttaja.sahkoposti
                    } `}
                    value={vastausTeksti}
                    onChange={(e) => setVastausTeksti(e.target.value)}
                    rows={5}
                    fullWidth
                  />
                  <Actions>
                    <Button
                      onClick={() => setConfirmAuki(true)}
                      disabled={!vastausTeksti.trim()}
                    >
                      {t("buttons.tukipyynto.laheta_ratkaise.yksi")}
                    </Button>
                    {/* S3 (10.6. B2): bulk vain kun kaikki käyttäjän avoimet omalla */}
                    {kaikkiOmassaKasittelyssa && (
                      <Button
                        variant="secondary"
                        onClick={() => setBulkConfirmAuki(true)}
                        disabled={!vastausTeksti.trim()}
                      >
                        {t("buttons.tukipyynto.laheta_ratkaise.kaikki")} (
                        {kayttajanAvoinMaara})
                      </Button>
                    )}
                  </Actions>
                </>
              ) : tukipyynto.kasittelijaUid &&
                tukipyynto.kasittelijaUid !== virkailija?.id ? (
                <OtaKasittelyynInfo>
                  {tukipyynto.kasittelijaNimi ?? "Toinen virkailija"}{" "}
                  {t("tekstit.kasittelee")}
                </OtaKasittelyynInfo>
              ) : (
                voikoKasitella && (
                  <Actions>
                    <Button onClick={handleOtaItselle}>
                      {t("buttons.ota_kasittelyyn")}
                    </Button>
                  </Actions>
                )
              )}
            </SektioHaitari>
          ) : (
            <SektioHaitari
              otsikko={
                yhdistaminenTehty
                  ? t("merge.tili_yhdistetty_otsikko")
                  : t("otsikot.tukipyynto_ratkaistu")
              }
              defaultOpen
            >
              <RatkaistuViesti>
                {yhdistaminenTehty
                  ? t("merge.yhdistetty")
                  : t("tapahtumat.ratkaistu")}{" "}
                {formatFinnishDate(tukipyynto.paivitetty)}
                {tukipyynto.kasittelijaNimi
                  ? ` · ${tukipyynto.kasittelijaNimi}`
                  : ""}
              </RatkaistuViesti>
              {tukipyynto.vastaus && (
                <>
                  <VastausOtsikko>{t("kayttajalle_viesti")}</VastausOtsikko>
                  <QuoteBox>{tukipyynto.vastaus}</QuoteBox>
                </>
              )}
            </SektioHaitari>
          )}
        </Vasen>

        <Oikea>
          {!onRatkaistu && (
            <KasittelijaPanel>
              <KasittelijaLabel>{t("labels.kasittelija")}</KasittelijaLabel>
              {tukipyynto.kasittelijaNimi ? (
                <KasittelijaNimi>{tukipyynto.kasittelijaNimi}</KasittelijaNimi>
              ) : (
                <EiKasittelijaa>{t("ei.kasittelijaa")}</EiKasittelijaa>
              )}
              {voikoKasitella && (
                <KasittelijaToiminto>
                  {onMinunKasittelyssani ? (
                    <Button
                      variant="secondaryNoBorder"
                      onClick={handlePalautaAvoimiin}
                    >
                      {t("buttons.palauta")}
                    </Button>
                  ) : (
                    <Button
                      variant="secondaryNoBorder"
                      onClick={handleOtaItselle}
                    >
                      {t("buttons.itselle")}
                    </Button>
                  )}
                </KasittelijaToiminto>
              )}
            </KasittelijaPanel>
          )}
          <KayttajaSidebar
            kayttaja={kayttaja}
            naytaTaysiKortti
            naytaSamanKayttajanAvoimet
            fromTukipyyntoId={tukipyynto.tikettinumero.replace("#", "")}
          />
          {muistiinpanotToteutettu && (
            <MuistiinpanoSticky kayttajaUid={kayttaja.uid} />
          )}
        </Oikea>
      </Layout>

      {/* Standard resolve modal */}
      <Modal
        appElementId="root"
        visible={confirmAuki}
        onEscKeyDown={() => setConfirmAuki(false)}
        scrollable={false}
      >
        <ModalContent>
          <ModalTitle>{t("otsikot.laheta_ratkaise")}</ModalTitle>
          <Text>
            {t("tekstit.laheta_ratkaise")} {tukipyynto.tikettinumero}{" "}
            {t("tekstit.ratkaistuksi")}?
          </Text>
        </ModalContent>
        <ModalFooter>
          <Button onClick={handleLahetaJaRatkaise}>Lähetä</Button>
          <Button variant="secondary" onClick={() => setConfirmAuki(false)}>
            Peruuta
          </Button>
        </ModalFooter>
      </Modal>

      {/* Bulk resolve modal */}
      <Modal
        appElementId="root"
        visible={bulkConfirmAuki}
        onEscKeyDown={() => setBulkConfirmAuki(false)}
        scrollable={false}
      >
        <ModalContent>
          <ModalTitle>{t("ratkaise_kaikki")}</ModalTitle>
          <Text>
            {t("tekstit.ratkaise_kaikki")} {kayttajanAvoinMaara}{" "}
            {t("tekstit.ratkaise_kaikki_2")}?
          </Text>
        </ModalContent>
        <ModalFooter>
          <Button onClick={handleLahetaJaRatkaiseKaikki}>
            {t("buttons.laheta_ratkaise")} {kayttajanAvoinMaara}{" "}
            {t("buttons.pyyntoa")}
          </Button>
          <Button variant="secondary" onClick={() => setBulkConfirmAuki(false)}>
            {t("buttons.peruuta")}
          </Button>
        </ModalFooter>
      </Modal>

      {/* Merge confirmation modal */}
      <Modal
        appElementId="root"
        visible={mergeConfirmAuki}
        onEscKeyDown={() => setMergeConfirmAuki(false)}
        scrollable={false}
      >
        <ModalContent>
          <ModalTitle>
            {mergeValinta === "yhdista"
              ? t("merge.modaali_otsikko_yhdista")
              : t("merge.modaali_otsikko_ala")}
          </ModalTitle>
          {mergeValinta === "yhdista" ? (
            kohde ? (
              <Text>
                {t("merge.modaali_teksti_yhdista", {
                  nimi: naytaNimi(kohde),
                  uid: kohde.customUid,
                  tiketti: tukipyynto.tikettinumero,
                })}
              </Text>
            ) : (
              <Text>
                {t("merge.modaali_teksti_eiosumaa", { uid: kohdeUid.trim() })}
              </Text>
            )
          ) : (
            <Text>
              {t("merge.modaali_teksti_ala", {
                tiketti: tukipyynto.tikettinumero,
              })}
            </Text>
          )}
        </ModalContent>
        <ModalFooter>
          <Button
            onClick={
              mergeValinta === "yhdista" ? handleYhdista : handleAlaYhdista
            }
          >
            {mergeValinta === "yhdista"
              ? t("merge.modaali_nappi_yhdista")
              : t("merge.modaali_nappi_ala")}
          </Button>
          <Button
            variant="secondary"
            onClick={() => setMergeConfirmAuki(false)}
          >
            {t("buttons.peruuta")}
          </Button>
        </ModalFooter>
      </Modal>

      {/* ✅ Document update confirmation modal */}
      <Modal
        appElementId="root"
        visible={documentUpdateConfirmAuki}
        onEscKeyDown={() => setDocumentUpdateConfirmAuki(false)}
        scrollable={false}
      >
        <ModalContent>
          <ModalTitle>
            {documentUpdateValinta === "approve"
              ? t("asiakirjan_paivitys.modaali_otsikko_hyvatty")
              : t("asiakirjan_paivitys.modaali_otsikko_hylatty")}
          </ModalTitle>
          <Text>
            {documentUpdateValinta === "approve"
              ? t("asiakirjan_paivitys.modaali_teksti_hyvatty", {
                  tiketti: tukipyynto?.tikettinumero,
                })
              : t("asiakirjan_paivitys.modaali_teksti_hylatty", {
                  tiketti: tukipyynto?.tikettinumero,
                })}
          </Text>
        </ModalContent>
        <ModalFooter>
          <Button
            onClick={
              documentUpdateValinta === "approve"
                ? handleApproveDocumentUpdate
                : handleDenyDocumentUpdate
            }
          >
            {documentUpdateValinta === "approve"
              ? t("asiakirjan_paivitys.modaali_nappi_hyvatty")
              : t("asiakirjan_paivitys.modaali_nappi_hylatty")}
          </Button>
          <Button
            variant="secondary"
            onClick={() => setDocumentUpdateConfirmAuki(false)}
          >
            {t("buttons.peruuta")}
          </Button>
        </ModalFooter>
      </Modal>
    </Page>
  );
}
